const SESSION_STORAGE_KEY = 'clareef_impersonation_session';
const EXPIRES_AT_STORAGE_KEY = 'clareef_impersonation_expires_at';

export function getImpersonationSessionId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(SESSION_STORAGE_KEY);
}

/**
 * Returns true when an impersonation session id is in localStorage AND its
 * persisted expiresAt is in the past. The backend is the source of truth for
 * expiry, but checking client-side lets us sign the impersonator out
 * immediately on tab reopen instead of waiting for the next API 401.
 */
export function isImpersonationSessionExpired(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const id = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!id) return false;
  const rawExpiresAt = localStorage.getItem(EXPIRES_AT_STORAGE_KEY);
  // Legacy session from before expiresAt was persisted — treat as expired so
  // the backend doesn't have to be the one to discover it via 401.
  if (!rawExpiresAt) return true;
  const expiresAt = new Date(rawExpiresAt);
  // Unparseable date — same logic, treat as expired.
  if (isNaN(expiresAt.getTime())) return true;
  return expiresAt.getTime() <= Date.now();
}

export function setImpersonationSession(sessionId: string, expiresAt?: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  if (expiresAt) {
    localStorage.setItem(EXPIRES_AT_STORAGE_KEY, expiresAt);
  } else {
    localStorage.removeItem(EXPIRES_AT_STORAGE_KEY);
  }
}

/** @deprecated use setImpersonationSession(sessionId, expiresAt). Kept for back-compat. */
export function setImpersonationSessionId(sessionId: string): void {
  setImpersonationSession(sessionId);
}

export function getImpersonationExpiresAt(): Date | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(EXPIRES_AT_STORAGE_KEY);
  if (!raw) return null;
  const date = new Date(raw);
  return isNaN(date.getTime()) ? null : date;
}

export function clearImpersonationSessionId(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(SESSION_STORAGE_KEY);
  localStorage.removeItem(EXPIRES_AT_STORAGE_KEY);
}

export function isImpersonating(): boolean {
  return !!getImpersonationSessionId();
}

export const IMPERSONATION_HEADER = 'X-Impersonation-Session';
export const IMPERSONATION_INVALID_CODE = 'IMPERSONATION_SESSION_INVALID';
